import { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useAI } from '@/context/AIContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Sparkles, TrendingUp, TrendingDown, AlertCircle,
  CheckCircle, Clock, Loader2, RefreshCw, Target,
  ArrowRight, Lightbulb, XCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SCORE_COLORS = {
  high: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: TrendingUp },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', icon: AlertCircle },
  low: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: TrendingDown }
};

function getScoreLevel(score) {
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
}

function ScoreGauge({ score, size = 'large' }) {
  const level = getScoreLevel(score);
  const rotation = (score / 100) * 180 - 90; // -90 to 90 degrees
  
  const sizes = {
    large: { width: 160, height: 80, stroke: 8, fontSize: 'text-3xl' },
    small: { width: 100, height: 50, stroke: 6, fontSize: 'text-xl' }
  };
  
  const s = sizes[size];
  
  return (
    <div className="relative" style={{ width: s.width, height: s.height + 20 }}>
      <svg width={s.width} height={s.height} viewBox={`0 0 ${s.width} ${s.height}`}>
        {/* Background arc */}
        <path
          d={`M ${s.stroke} ${s.height - s.stroke} A ${s.width/2 - s.stroke} ${s.height - s.stroke*2} 0 0 1 ${s.width - s.stroke} ${s.height - s.stroke}`}
          fill="none"
          stroke="#E6E0D9"
          strokeWidth={s.stroke}
          strokeLinecap="round"
        />
        {/* Score arc */}
        <path
          d={`M ${s.stroke} ${s.height - s.stroke} A ${s.width/2 - s.stroke} ${s.height - s.stroke*2} 0 0 1 ${s.width - s.stroke} ${s.height - s.stroke}`}
          fill="none"
          stroke={level === 'high' ? '#10B981' : level === 'medium' ? '#F59E0B' : '#EF4444'}
          strokeWidth={s.stroke}
          strokeLinecap="round"
          strokeDasharray={`${(score / 100) * (Math.PI * (s.width/2 - s.stroke))} ${Math.PI * (s.width/2 - s.stroke)}`}
        />
        {/* Needle */}
        <line
          x1={s.width/2}
          y1={s.height - s.stroke}
          x2={s.width/2 + Math.cos(rotation * Math.PI / 180) * (s.height - s.stroke*3)}
          y2={s.height - s.stroke - Math.sin(rotation * Math.PI / 180) * (s.height - s.stroke*3)}
          stroke="#2C2420"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Center dot */}
        <circle cx={s.width/2} cy={s.height - s.stroke} r="4" fill="#2C2420" />
      </svg>
      <div className={cn("absolute bottom-0 left-0 right-0 text-center font-heading", s.fontSize)}>
        {score}
      </div>
    </div>
  );
}

export default function AILeadScore({ lead, onScoreUpdate }) {
  const { token } = useAuth();
  const { aiEnabled } = useAI();
  const [isLoading, setIsLoading] = useState(false);
  const [scoreData, setScoreData] = useState(lead?.aiScore ? {
    score: lead.aiScore,
    confidence: lead.aiScoreConfidence,
    summary: lead.aiScoreSummary,
    scoredAt: lead.aiScoredAt,
    hasScore: true
  } : null);
  const [showDetails, setShowDetails] = useState(false);
  const [fullScore, setFullScore] = useState(null);

  if (!aiEnabled) return null;

  const generateScore = async () => {
    setIsLoading(true);
    try {
      const response = await api.post(token, '/ai/lead-score', { leadId: lead.id });
      setScoreData({ ...response.data, hasScore: true });
      setFullScore(response.data);
      setShowDetails(true);
      
      if (onScoreUpdate) {
        onScoreUpdate(lead.id, response.data);
      }
      
      toast.success('Lead scored successfully');
    } catch (error) {
      console.error('Scoring error:', error);
      toast.error('Failed to score lead');
    }
    setIsLoading(false);
  };

  const level = scoreData?.score ? getScoreLevel(scoreData.score) : null;
  const colors = level ? SCORE_COLORS[level] : null;

  // If no score yet
  if (!scoreData?.hasScore) {
    return (
      <div className="bg-gradient-to-br from-[#F9F7F5] to-white rounded-xl border border-[#E6E0D9] p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#4A3B32]/10 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#4A3B32]" />
            </div>
            <h4 className="font-medium text-[#2C2420]">AI Lead Score</h4>
          </div>
        </div>
        
        <p className="text-sm text-[#5D524D] mb-4">
          Get an AI-powered score to evaluate this lead's potential and recommended actions.
        </p>
        
        <Button
          onClick={generateScore}
          disabled={isLoading}
          className="w-full bg-[#4A3B32] hover:bg-[#3A2E26]"
          data-testid="generate-ai-score-btn"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Generate AI Score
            </>
          )}
        </Button>
      </div>
    );
  }

  return (
    <div className={cn(
      "rounded-xl border p-4 transition-all",
      colors.bg, colors.border
    )}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", colors.bg)}>
            <colors.icon className={cn("w-4 h-4", colors.text)} />
          </div>
          <h4 className="font-medium text-[#2C2420]">AI Lead Score</h4>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={generateScore}
          disabled={isLoading}
          className="h-8 px-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4" />
          )}
        </Button>
      </div>

      {/* Score Display */}
      <div className="flex items-center justify-center mb-4">
        <ScoreGauge score={scoreData.score} />
      </div>

      {/* Confidence Badge */}
      <div className="flex items-center justify-center gap-2 mb-3">
        <span className={cn(
          "px-3 py-1 rounded-full text-xs font-medium",
          scoreData.confidence === 'high' ? 'bg-green-100 text-green-700' :
          scoreData.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
          'bg-gray-100 text-gray-600'
        )}>
          {scoreData.confidence === 'high' ? (
            <><CheckCircle className="w-3 h-3 inline mr-1" /> High Confidence</>
          ) : scoreData.confidence === 'medium' ? (
            <><AlertCircle className="w-3 h-3 inline mr-1" /> Medium Confidence</>
          ) : (
            <><Clock className="w-3 h-3 inline mr-1" /> Low Confidence</>
          )}
        </span>
      </div>

      {/* Summary */}
      {scoreData.summary && (
        <p className="text-sm text-[#5D524D] text-center mb-3">
          {scoreData.summary.length > 150 
            ? scoreData.summary.slice(0, 150) + '...' 
            : scoreData.summary}
        </p>
      )}

      {/* Toggle Details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="w-full text-center text-sm text-[#4A3B32] hover:underline flex items-center justify-center gap-1"
      >
        {showDetails ? 'Hide' : 'Show'} Details
        <ArrowRight className={cn("w-3 h-3 transition-transform", showDetails && "rotate-90")} />
      </button>

      {/* Details Panel */}
      {showDetails && fullScore && (
        <div className="mt-4 pt-4 border-t border-[#E6E0D9] space-y-4">
          {/* Factors */}
          {fullScore.factors && fullScore.factors.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-[#8C8078] uppercase tracking-wider mb-2">
                Key Factors
              </h5>
              <div className="space-y-2">
                {fullScore.factors.map((factor, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm">
                    {factor.impact === 'positive' ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : factor.impact === 'negative' ? (
                      <XCircle className="w-4 h-4 text-red-500" />
                    ) : (
                      <AlertCircle className="w-4 h-4 text-gray-400" />
                    )}
                    <span className="flex-1 text-[#2C2420]">{factor.factor}</span>
                    <span className="text-xs text-[#8C8078]">
                      Weight: {factor.weight}/10
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Actions */}
          {fullScore.nextActions && fullScore.nextActions.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-[#8C8078] uppercase tracking-wider mb-2">
                <Lightbulb className="w-3 h-3 inline mr-1" />
                Recommended Actions
              </h5>
              <ul className="space-y-1">
                {fullScore.nextActions.map((action, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#2C2420]">
                    <Target className="w-3 h-3 mt-1 text-[#4A3B32]" />
                    {action}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Scored At */}
          {scoreData.scoredAt && (
            <p className="text-xs text-[#8C8078] text-center">
              Scored {new Date(scoreData.scoredAt).toLocaleDateString('en-AU', {
                day: 'numeric', month: 'short', year: 'numeric', 
                hour: '2-digit', minute: '2-digit'
              })}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// Compact version for use in cards/lists
export function AILeadScoreBadge({ score, confidence, onClick }) {
  if (!score) return null;
  
  const level = getScoreLevel(score);
  const colors = SCORE_COLORS[level];
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-all hover:scale-105",
        colors.bg, colors.text
      )}
      title={`AI Score: ${score}/100 (${confidence} confidence)`}
    >
      <Sparkles className="w-3 h-3" />
      {score}
    </button>
  );
}
